import { BillingNotifications } from '@features/Admin/Billing/ui/BillingNotifications';
import {
  IconButton,
  Box,
  AppBar,
  Chip,
  useMediaQuery,
  Toolbar,
  styled,
  Stack,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconMenu2 } from '@tabler/icons-react';

import Profile from './Profile';

import Navigation from './Navigation';
import { RootState, useAppDispatch, useAppSelector } from '@shared/store';
import { actions } from '@shared/store/ducks';
import BreadcrumbV2 from '../../shared/breadcrumb/BreadcrumbV2';
import { useTranslation } from 'react-i18next';
import { generatePath, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconBack } from '@shared/assets/images/svgs';
import { AuthenticatedPath } from '@shared/constants/routes.constants';
import CampaignStatusButtonV2 from '@pages/Admin/Main/Campaign/ui/CampaignStatusButton/CampaignStatusButtonV2';
import { formatCampaignStatusLabel } from '@pages/Admin/Main/Campaign/ui/CampaignStatusButton/utils';
import { useStartStopContext } from '@features/Admin/Campaign/contexts/useStartStop.context';
import { DesignChooser } from '@shared/designVariants/DesignChooser';
import { SidebarTabNames } from '@pages/Admin/Campaign/ui/constants';

const Header = ({ showDesignChooser = false }: { showDesignChooser?: boolean }) => {
  const dispatch = useAppDispatch();
  const lgUp = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const { callStartStopFunction, campaignContext, isDisabledCampaignRun, disabledReason } =
    useStartStopContext();
  // drawer
  const BCrumb = useAppSelector((state: RootState) => state.breadcrumbs);
  const customizer = useAppSelector((state: RootState) => state.customizer);
  const isCallDetailPage = Boolean(
    matchPath({ path: AuthenticatedPath.CALL_DETAIL, end: true }, location.pathname),
  );
  const isCampaignEditHeader = customizer.isEditCampaignView && !isCallDetailPage;
  const callDetailBackPath = campaignId
    ? `${generatePath(AuthenticatedPath.EDIT_CAMPAIGN_V2, { campaignId })}?tab=${encodeURIComponent(
        SidebarTabNames.ONGOING_CALLS,
      )}`
    : AuthenticatedPath.CAMPAIGN;

  const AppBarStyled = styled(AppBar)(({ theme }) => ({
    boxShadow: 'none',
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
    [theme.breakpoints.up('lg')]: {
      minHeight: customizer.TopbarHeight,
    },
  }));
  const ToolbarStyled = styled(Toolbar)(({ theme }) => ({
    width: '100%',
    color: theme.palette.text.secondary,
  }));

  const BackButtonStyled = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    boxShadow: `0px 2px 4px ${alpha(theme.palette.extendedGrey.neutral, 0.3)}`,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.extendedGrey.lighter}`,
    width: 30,
    height: 30,
    'svg path': {
      stroke: theme.palette.getContrastText(theme.palette.background.default),
    },
    '&:hover': {
      backgroundColor: theme.palette.extendedGrey?.lightest,
      'svg path': {
        stroke: theme.palette.primary.main,
      },
    },
  }));

  return (
    <AppBarStyled position="sticky" color="default">
      <ToolbarStyled>
        {!lgUp && (
          <IconButton
            aria-label="Open navigation"
            onClick={() => dispatch(actions.customizer.setMobileSidebar(true))}>
            <IconMenu2 />
          </IconButton>
        )}
        {isCampaignEditHeader || isCallDetailPage ? (
          <IconButton
            size="small"
            onClick={() =>
              navigate(isCampaignEditHeader ? AuthenticatedPath.CAMPAIGN : callDetailBackPath)
            }>
            <BackButtonStyled>
              <IconBack />
            </BackButtonStyled>
          </IconButton>
        ) : null}
        {BCrumb.iShown && (
          <BreadcrumbV2
            title={t(BCrumb.pageTitle, { defaultValue: '', ...BCrumb.titleValue })}
            campaignType={BCrumb.campaignType}
            icon={BCrumb.icon}
          />
        )}

        {lgUp ? <Navigation /> : null}

        <Box flexGrow={1} />
        <Stack spacing={1} direction="row" alignItems="center">
          <BillingNotifications />
          {showDesignChooser && <DesignChooser />}
          {!isCampaignEditHeader && <Profile />}
          {isCampaignEditHeader && campaignContext?.id && (
            <Tooltip
              title={isDisabledCampaignRun && disabledReason ? disabledReason : ''}
              arrow
              placement="bottom">
              <Box data-testid="campaign-edit-start-stop-anchor" sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  size="small"
                  data-testid="campaign-edit-status"
                  label={formatCampaignStatusLabel(campaignContext.status)}
                />
                <CampaignStatusButtonV2
                  minimized={false}
                  onClick={callStartStopFunction}
                  campaign={campaignContext}
                  disabled={
                    isDisabledCampaignRun ||
                    (campaignContext.status as string | undefined) === 'finishing'
                  }
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </ToolbarStyled>
    </AppBarStyled>
  );
};

export default Header;
